/* eslint-disable no-unused-vars */
import axios from "axios";
import moment from "moment-timezone";
import { useState, useRef, useEffect } from "react";
import {
  BASEURL,
  convertTime,
  timezoneFormatter,
  convertTimeIntoSselectedTZ,
} from "../../../utilites";
// CSS
import "react-datepicker/dist/react-datepicker.css";
import allActions from "../../../Store/action";
import { useDispatch, useSelector } from "react-redux";
import Message from "../fields/Message";
import { CALL_ACTION_TYPE } from "../../../Store/call/Type";
import ReactSelect from "react-select";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { FaVideo, FaPhone, FaCalendarAlt } from "react-icons/fa";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

const americanTimeZones = moment.tz.zonesForCountry("US");
const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export default function OnboardingAppointmentForm({
  lead,
  setStep,
  license,
  isQuestionnaireCompleted,
  isMessageSent,
  isPdfDownloaded,
}) {
  const [message, setMessage] = useState({ show: false });
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [timezone, setTimezone] = useState(
    moment.tz.guess().startsWith("America/")
      ? moment.tz.guess()
      : "America/Adak"
  );
  const [timezoneName, setTimezoneName] = useState(
    moment.tz.guess().startsWith("America/")
      ? moment.tz.guess()
      : "America/Adak"
  );
  const [selectedTimezone, setSelectedTimezone] = useState(moment.tz.guess());
  const [startDate, setStartDate] = useState();
  const [duration, setDuration] = useState(45);
  const [appointmentType, setAppointmentType] = useState("video");
  const [{ videoCallRoom }] = useSelector((state) => [state.call]);
  const [taxProAvailability, settaxProAvailability] = useState(false);
  const topTimezones = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
  ];

  const [errors, setErrors] = useState({
    timezone: false,
    startDate: false,
    duration: false,
    general: "",
  });

  const button = useRef();
  const dispatch = useDispatch();

  const [selectedTime, setSelectedTime] = useState(null);
  const [weekDates, setWeekDates] = useState([]);
  // UD-296: busy ranges merged from UNK appointments + Google freebusy for
  // the visible 3-day window. renderTimeButtons skips slots that overlap any.
  // [{ start: ms, end: ms }, ...]
  const [busyRanges, setBusyRanges] = useState([]);

  useEffect(() => {
    const availableDates = [];
    const maxDaysToCheck = 14;
    const startFrom = moment().add(1, "day");

    let checked = 0;
    let dayOffset = 0;

    while (checked < 3 && dayOffset <= maxDaysToCheck) {
      const date = startFrom.clone().add(dayOffset, "days");
      const dayName = date.format("dddd");

      const availability =
        lead?.userpro?.user?.availability?.time_ranges?.[dayName];

      if (availability && availability.length > 0) {
        availableDates.push(date);
        checked++;
      }

      dayOffset++;
    }

    setWeekDates(availableDates);
  }, [lead?.userpro?.user?.availability?.time_ranges]);

  // UD-296: pull busy ranges for the visible week from the availability
  // endpoint. We don't need its returned slot list (renderTimeButtons builds
  // its own from time_ranges) — we just need the busy windows so we can
  // filter out time buttons that overlap. Inferring busy from the
  // (raw time_ranges expansion) minus (returned slot list) gives us busy
  // exactly without changing the render path.
  useEffect(() => {
    if (!weekDates || weekDates.length === 0 || !lead?.uuid) {
      setBusyRanges([]);
      return;
    }
    const fromUtc = weekDates[0].clone().tz(timezone).startOf("day").toISOString();
    const toUtc = weekDates[weekDates.length - 1]
      .clone()
      .tz(timezone)
      .endOf("day")
      .toISOString();
    (async () => {
      try {
        const { data: resp } = await axios.get(
          `${BASEURL}/api/booking/${lead.uuid}/availability`,
          { params: { from: fromUtc, to: toUtc, timezone, duration: 30 } }
        );
        const returnedSlotKeys = new Set(
          (resp?.data?.slots || []).map((s) => moment.utc(s.start).valueOf())
        );
        // Build the raw grid the same way renderTimeButtons does, then any
        // slot in the raw grid that's NOT in returnedSlotKeys is busy.
        const taxProTZ = lead?.userpro?.user?.settings?.timezone || "UTC";
        const ranges = [];
        weekDates.forEach((dayMoment) => {
          const dayName = dayMoment.format("dddd");
          const dayRanges = lead?.userpro?.user?.availability?.time_ranges?.[dayName];
          if (!dayRanges) return;
          dayRanges.forEach((range) => {
            const start = moment.tz(
              `${dayMoment.format("YYYY-MM-DD")} ${range.from}`,
              "YYYY-MM-DD hh:mm A",
              taxProTZ
            );
            const end = moment.tz(
              `${dayMoment.format("YYYY-MM-DD")} ${range.to}`,
              "YYYY-MM-DD hh:mm A",
              taxProTZ
            );
            let current = start.clone();
            while (current.isBefore(end)) {
              const slotMs = current.valueOf();
              if (!returnedSlotKeys.has(slotMs)) {
                ranges.push({ start: slotMs, end: slotMs + 30 * 60 * 1000 });
              }
              current.add(30, "minutes");
            }
          });
        });
        setBusyRanges(ranges);
      } catch (err) {
        console.error("availability endpoint failed; showing all slots:", err?.message);
        setBusyRanges([]);
      }
    })();
  }, [weekDates, timezone, lead?.uuid, lead?.userpro?.user?.availability?.time_ranges, lead?.userpro?.user?.settings?.timezone]);

  useEffect(() => {
    const defaultTZ = moment.tz.guess().startsWith("America/")
      ? moment.tz.guess()
      : "America/Adak";

    setTimezone(defaultTZ);
    setTimezoneName(defaultTZ);
    setSelectedTimezone(defaultTZ);

    convertTimesToSelectedTimezone({
      value: defaultTZ,
      label: timezoneFormatter(defaultTZ),
    });
  }, []);

  const handleTimeClick = (datetimeMoment) => {
    const iso = datetimeMoment.clone().toISOString();
    setSelectedTime(iso);
    // Set startDate the same way as the main appointment form:
    // parse the user-TZ formatted string so convertTimeIntoSselectedTZ works correctly
    const timeStr = datetimeMoment.format("YYYY-MM-DD HH:mm:ss");
    setStartDate(moment(timeStr).toDate());
    console.log("Selected datetime:", timeStr);
  };

  const renderTimeButtons = (dayMoment, selectedTimezone) => {
    const dayName = dayMoment?.format("dddd");
    if (!dayName || !taxProAvailability?.[dayName]) return null;

    const buttons = [];

    taxProAvailability[dayName].forEach((range) => {
      const taxProTZ = lead?.userpro?.user?.settings?.timezone || "UTC";

      const start = moment.tz(
        `${dayMoment.format("YYYY-MM-DD")} ${range.from}`,
        "YYYY-MM-DD hh:mm A",
        taxProTZ
      );

      const end = moment.tz(
        `${dayMoment.format("YYYY-MM-DD")} ${range.to}`,
        "YYYY-MM-DD hh:mm A",
        taxProTZ
      );

      let current = start.clone();

      while (current.isBefore(end)) {
        const slotStartMs = current.valueOf();
        const slotEndMs = slotStartMs + 30 * 60 * 1000;
        // UD-296 + booked-slot UX: render busy slots as a disabled "Booked"
        // entry instead of skipping, so the customer sees that there's no gap
        // bug in the picker — those times exist but are taken.
        const isBusy = busyRanges.some(
          (b) => slotStartMs < b.end && b.start < slotEndMs
        );

        const convertedTime = current.clone().tz(selectedTimezone);
        const displayTime = convertedTime.format("hh:mm A");
        const slotIso = convertedTime.clone().toISOString();

        if (isBusy) {
          buttons.push(
            <div
              key={slotIso}
              className="m-1 px-0 py-2 rounded time_btn text-secondary text-center"
              style={{ cursor: "not-allowed", backgroundColor: "#e9ecef" }}
              aria-disabled="true"
              title="This time slot is already booked"
            >
              {displayTime}
              <span
                className="badge ms-2 text-white"
                style={{ backgroundColor: "#20c997", fontSize: "0.65rem" }}
              >
                Booked
              </span>
            </div>
          );
          current.add(30, "minutes");
          continue;
        }

        const isSelected = selectedTime === slotIso;

        buttons.push(
          <div
            key={slotIso}
            className={`m-1 px-0 py-2 rounded time_btn ${
              isSelected ? "bg-primary text-white" : "bg-light text-dark"
            }`}
            onClick={() => handleTimeClick(convertedTime)}
          >
            {displayTime}
          </div>
        );

        current.add(30, "minutes");
      }
    });

    return buttons;
  };

  useEffect(() => {
    if (videoCallRoom.status === "idle") resetMessage();

    if (!videoCallRoom.status) {
      if (button?.current?.disabled) button.current.disabled = false;
      resetMessage();
      setMessage({
        show: true,
        type: "Error",
        text: videoCallRoom.message,
      });
      changeLoading(false);
    }

    if (videoCallRoom.status === true) {
      if (selectedTime && videoCallRoom?.data?.meetingID) {
        bookVideoAppoinment(videoCallRoom?.data?.meetingID);
      } else {
        if (button?.current?.disabled) button.current.disabled = false;
        resetMessage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoCallRoom, videoCallRoom.message, videoCallRoom.status]);

  const bookVideoAppoinment = (roomId) => {
    const taxProTimezone = lead?.userpro?.user?.settings?.timezone || "UTC";
    const end_date = new Date(startDate);
    end_date.setMinutes(startDate.getMinutes() + duration);

    if (button?.current?.disabled) button.current.disabled = true;
    if (errors.general !== "") setErrors({ ...errors, general: "" });

    const appointmentObj = {
      timezone: taxProTimezone,
      startDate: convertTimeIntoSselectedTZ(timezone, taxProTimezone, startDate),
      duration,
      roomId,
      endDate: convertTimeIntoSselectedTZ(timezone, taxProTimezone, end_date),
    };

    axios
      .post(
        `${BASEURL}/api/booking/${lead?.uuid}/${lead?.user?.uuid}/${lead?.userpro?.user?.uuid}`,
        appointmentObj
      )
      .then((res) => {
        if (license && !isQuestionnaireCompleted) {
          setStep((prev) => prev + 2);
        } else if (license && isQuestionnaireCompleted && !isMessageSent) {
          setStep((prev) => prev + 3);
        } else if (
          license &&
          isQuestionnaireCompleted &&
          isMessageSent &&
          !isPdfDownloaded
        ) {
          setStep((prev) => prev + 4);
        } else if (
          license &&
          isQuestionnaireCompleted &&
          isMessageSent &&
          isPdfDownloaded
        ) {
          setStep((prev) => prev + 5);
        } else {
          setStep((prev) => prev + 1);
        }
        changeLoading(false);
        dispatch(allActions.dashboard.getUpcomingAppointments());
        dispatch(allActions.user.ReGetUserData());
      })
      .catch((error) => {
        if (button?.current?.disabled) button.current.disabled = false;
        console.error(error.message);
        changeLoading(false);
        if (errors.general === "")
          setErrors({
            ...errors,
            general: error?.response?.data?.message || error.message,
          });
      });
  };

  const changeLoading = (status) => {
    dispatch(allActions.global.Loader(status));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessage();
    if (button?.current?.disabled) button.current.disabled = true;
    if (errors.general !== "") setErrors({ ...errors, general: "" });
    changeLoading(true);

    const end_date = new Date(startDate);
    end_date.setMinutes(startDate.getMinutes() + duration);
    // UD-297: backend bookAppointment handles ALL calendar pushes
    // (Google + Outlook) after the UNK row commits. Frontend no longer
    // makes any /add-event calls.
    await addAppointment(end_date);
  };

  const addAppointment = async (end_date) => {
    if (appointmentType === "video") {
      const meetingData = {
        taxProEmail: lead?.userpro?.user?.email || lead?.user?.email,
        email: lead?.user?.email,
        startTime: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
        duration,
        timezone,
      };

      dispatch(
        allActions.call.dispatchToStore({
          type: CALL_ACTION_TYPE.videoCallRoomIdle,
        })
      );
      dispatch(allActions.call.createRoom(meetingData));
      return;
    }

    const taxProTimezone = lead?.userpro?.user?.settings?.timezone || "UTC";

    const appointmentObj = {
      timezone: taxProTimezone,
      startDate: convertTimeIntoSselectedTZ(timezone, taxProTimezone, startDate),
      duration,
      endDate: convertTimeIntoSselectedTZ(timezone, taxProTimezone, end_date),
    };

    axios
      .post(
        `${BASEURL}/api/booking/${lead?.uuid}/${lead?.user?.uuid}/${lead?.userpro?.user?.uuid}`,
        appointmentObj
      )
      .then((res) => {
        changeLoading(false);

        if (license && !isQuestionnaireCompleted) {
          setStep((prev) => prev + 2);
        } else if (license && isQuestionnaireCompleted && !isMessageSent) {
          setStep((prev) => prev + 3);
        } else if (
          license &&
          isQuestionnaireCompleted &&
          isMessageSent &&
          !isPdfDownloaded
        ) {
          setStep((prev) => prev + 4);
        } else if (
          license &&
          isQuestionnaireCompleted &&
          isMessageSent &&
          isPdfDownloaded
        ) {
          setStep((prev) => prev + 5);
        } else {
          setStep((prev) => prev + 1);
        }

        dispatch(allActions.dashboard.getUpcomingAppointments());
        dispatch(allActions.user.ReGetUserData());
      })
      .catch((error) => {
        if (button?.current?.disabled) button.current.disabled = false;
        changeLoading(false);
        if (errors.general === "")
          setErrors({
            ...errors,
            general: error?.response?.data?.message || error.message,
          });
      });

    setTimezone(
      moment.tz.guess().startsWith("America/")
        ? moment.tz.guess()
        : "America/Adak"
    );
    setDuration(45);
    setAppointmentType("video");
  };

  const resetMessage = () => {
    changeLoading(false);
    setMessage({ show: false });
  };

  useEffect(() => {
    let taxProAvailability = false;
    for (let index = 0; index < dayNames.length; index++) {
      if (lead?.userpro?.user?.availability?.time_ranges[dayNames[index]]) {
        taxProAvailability = true;
        break;
      }
    }
    if (taxProAvailability) {
      settaxProAvailability(lead?.userpro?.user?.availability?.time_ranges);
      istaxProAvailable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.userpro?.user?.availability?.time_ranges]);

  const istaxProAvailable = () => {
    let i = 0;
    let date = new Date();
    while (i < 1) {
      date.setDate(date.getDate() + 1);
      const day = dayNames[date.getDay()];
      if (lead?.userpro?.user?.availability?.time_ranges[day] !== null) {
        setStartDate(date);
        const currentDate = moment(date);
        const dayOfWeek = currentDate.format("dddd");
        const slotDuration = 45;
        const timeSlots = [];
        const originaltimeSlots = [];

        lead?.userpro?.user?.availability?.time_ranges[dayOfWeek]?.forEach(
          (v, i) => {
            originaltimeSlots.push(
              ...getTimeSlots(
                v.from,
                v.to,
                slotDuration,
                currentDate.format("YYYY-MM-DD")
              ).originaltimeSlots
            );
            timeSlots.push(
              ...getTimeSlots(
                v.from,
                v.to,
                slotDuration,
                currentDate.format("YYYY-MM-DD")
              ).timeSlots
            );
          }
        );

        i++;
      }
    }
  };

  const getTimeSlots = (startTime, endTime, slotDuration, currentDate) => {
    const timeSlots = [];
    const originaltimeSlots = [];

    const startDateTime = moment(
      `${currentDate} ${startTime}`,
      "YYYY-MM-DD HH:mm:ss"
    );
    const endDateTime = moment(
      `${currentDate} ${endTime}`,
      "YYYY-MM-DD HH:mm:ss"
    );
    let currentTime = startDateTime;

    while (currentTime.isSameOrBefore(endDateTime)) {
      const currentTimeWithTZ = moment.tz(
        currentTime.format("YYYY-MM-DDTHH:mm:ss"),
        lead?.userpro?.user?.settings?.timezone
      );

      originaltimeSlots.push(
        moment(currentTimeWithTZ).format("YYYY-MM-DD HH:mm:ss")
      );
      timeSlots.push(
        moment(currentTimeWithTZ).tz(timezone).format("YYYY-MM-DD HH:mm:ss")
      );
      currentTime.add(slotDuration, "minutes");
    }
    originaltimeSlots.pop();
    timeSlots.pop();
    return {
      originaltimeSlots,
      timeSlots,
    };
  };

  const customStyles = {
    option: (provided) => ({
      ...provided,
      textAlign: "left",
    }),
    singleValue: (provided) => ({
      ...provided,
      textAlign: "left",
    }),
    placeholder: (provided) => ({
      ...provided,
      textAlign: "left",
      fontSize: 14,
    }),
  };

  const convertTimesToSelectedTimezone = (selectedOption) => {
    setTimezone(selectedOption.value);
    setTimezoneName(selectedOption.label);
    setSelectedTimezone(selectedOption.value);
  };

  return (
    <>
      {taxProAvailability && (
        <div className="appointmentsform needs-validation w-100">
          <div className="row">
            {message.show ? <Message message={message} /> : <></>}
          </div>

          <div className="d-flex justify-content-center mb-2">
            <div style={{ minWidth: "300px" }}>
              <ReactSelect
                id="users"
                className="basic-single"
                placeholder="Select Timezone"
                styles={customStyles}
                closeMenuOnSelect={true}
                options={[
                  ...topTimezones.map((v) => ({
                    value: v,
                    label: timezoneFormatter(v),
                  })),
                  ...americanTimeZones.map((v) => ({
                    value: v,
                    label: timezoneFormatter(v),
                  })),
                ]}
                onChange={(v) => convertTimesToSelectedTimezone(v)}
                value={{
                  value: timezone,
                  label: `${timezoneFormatter(timezoneName)} (${convertTime(
                    timezone,
                    "h:mm A"
                  )})`,
                }}
              />
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-center mb-3">
            <div
              className={`me-3 cursor-pointer ${
                currentSlideIndex === 0 ? "text-light" : "text-primary"
              }`}
              disabled={currentSlideIndex === 0}
              onClick={() =>
                setCurrentSlideIndex((prev) => Math.max(0, prev - 1))
              }
            >
              <MdKeyboardArrowLeft size={20} />
            </div>
            <div className="fw-semibold fs-6 text-dark">
              {weekDates[currentSlideIndex]?.format("dddd, MMMM D, YYYY")}
            </div>
            <div
              className={`ms-3 cursor-pointer ${
                currentSlideIndex === weekDates.length - 1
                  ? "text-light"
                  : "text-primary"
              }`}
              disabled={currentSlideIndex === weekDates.length - 1}
              onClick={() =>
                setCurrentSlideIndex((prev) =>
                  Math.min(weekDates.length - 1, prev + 1)
                )
              }
            >
              <MdKeyboardArrowRight size={20} />
            </div>
          </div>

          <div className="position-relative" style={{ minHeight: "70px" }}>
            <div
              key={weekDates[currentSlideIndex]?.format("YYYY-MM-DD")}
              className="fade-slide d-flex flex-wrap justify-content-center gap-2"
            >
              {renderTimeButtons(weekDates[currentSlideIndex], selectedTimezone)}
            </div>
          </div>

          <div className="d-flex justify-content-center my-4 align-items-center gap-4">
            <div
              className={`btn btn-sm ${
                appointmentType === "video"
                  ? "btn-primary text-white"
                  : "btn-outline-light text-dark"
              }`}
              onClick={() => setAppointmentType("video")}
            >
              <FaVideo className="me-2" />
              Video Appointment
            </div>
            <div
              className={`btn btn-sm ${
                appointmentType === "call"
                  ? "btn-primary text-white"
                  : "btn-outline-light text-dark"
              }`}
              onClick={() => setAppointmentType("call")}
            >
              <FaPhone className="me-2" />
              Phone Appointment
            </div>
          </div>

          <div className="d-flex justify-content-center mb-5">
            <button
              className="btn btn-primary d-flex align-items-center gap-2 px-4"
              disabled={!selectedTime}
              onClick={handleSubmit}
            >
              <FaCalendarAlt />
              Book Call
            </button>
          </div>

          {errors.general && (
            <div className="alert alert-danger text-center">
              {errors.general}
            </div>
          )}

          <div className="d-flex justify-content-between w-100 mt-3 px-3">
            <button
              className="btn btn-outline-primary d-flex align-items-center gap-2"
              type="button"
              onClick={() => setStep((prev) => prev - 1)}
            >
              <FiArrowLeft /> Back
            </button>

            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              type="button"
              onClick={() => {
                if (license && !isQuestionnaireCompleted) {
                  setStep((prev) => prev + 2);
                } else if (
                  license &&
                  isQuestionnaireCompleted &&
                  !isMessageSent
                ) {
                  setStep((prev) => prev + 3);
                } else if (
                  license &&
                  isQuestionnaireCompleted &&
                  isMessageSent &&
                  !isPdfDownloaded
                ) {
                  setStep((prev) => prev + 4);
                } else if (
                  license &&
                  isQuestionnaireCompleted &&
                  isMessageSent &&
                  isPdfDownloaded
                ) {
                  setStep((prev) => prev + 5);
                } else {
                  setStep((prev) => prev + 1);
                }
              }}
            >
              Next Step <FiArrowRight />
            </button>
          </div>

          {errors.general && errors.general !== "" && (
            <div className="row">
              <div className="mt-3">
                <div className="alert alert-danger" role="alert">
                  {errors.general}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}